<template>
  <v-container
    fluid
    class="content fill-height"
  >
    <v-row
      no-gutters
      justify="space-between"
      class="content__row"
    >
      <v-col
        ref="previewWindow"
        :style="{ 'height': previewWindowHeight }"
        lg="7"
        md="100%"
        class="content__col px-5 pt-5"
      >
        <v-skeleton-loader
          v-show="step === STEP.UPLOADING"
          class="content__skeleton-preview"
          type="image"
        />
        <div
          v-show="step === STEP.PREVIEW"
          class="fill-height"
        >
          <div
            v-show="pdf.preview && pdf.preview !== 'error' && pdf.preview !== 'none'"
            class="fill-height"
          >
            <div
              class="bg-white content__preview"
              :style="{ height: pdf.size.height ? `${pdf.size.height}px` : '100%' }"
            >
              <pdf-viewer
                ref="pdfViewer"
                :source="viewerSrc"
                :page="pdf.page.current"
                class="content__pdf"
                :class="{ 'content__pdf--animate-show': pdf.animate === 'launch' }"
                @rendered="onPdfRendered"
                @rendering-failed="onRenderFailed"
              />
              <ResizeObserver @notify="onContentResize" />
            </div>
            <div
              v-if="pdf.page.count > 1"
              :key="pdf.page.count"
              class="mt-4 content__pages"
              :class="{ 'content__pages--animate': pdf.page.count }"
              @mouseover="onPagesHover"
              @mouseleave="onPagesLeave"
            >
              <div
                ref="pagesBackground"
                class="content__pages__background"
              >
                <v-pagination
                  v-model="pdf.page.current"
                  :total-visible="9"
                  :color="appConfig.color.secondary"
                  :length="pdf.page.count"
                />
              </div>
            </div>
          </div>
          <div
            v-show="!pdf.preview"
            :style="{ 'height': a4Height.inner }"
            class="content__preview-empty"
          />
          <div
            v-show="pdf.preview === 'error' || pdf.preview === 'none'"
            :style="{ 'height': a4Height.inner }"
            class="content__preview-empty content__preview-empty--error"
          >
            {{ pdf.preview === 'error'
              ? 'Для этого документа просмотр недоступен'
              : 'Выберите документ для отображения'
            }}
          </div>
        </div>
      </v-col>

      <v-col
        lg="5"
        md="100%"
        class="content__bottom-col fill-height pa-5"
      >
        <div v-if="step === STEP.UPLOADING">
          <v-skeleton-loader
            class="content__skeleton-list mb-10"
            type="card-heading, list-item@3, card-heading, image"
          />
          <v-skeleton-loader
            class="content__skeleton-list"
            type="card-heading, list-item-two-line"
          />
        </div>
        <div
          v-else
          class="content__list-and-options"
          :class="{ 'content__list-and-options--ie': browserIsIE() }"
        >
          <DocumentList
            small
            :doc-list="docList"
            :certificate-file.sync="options.certificate"
            :loading="docListLoading"
            :selected-id="pdf.previewedId"
            class="content__card mb-10"
            @input="options.plugins = $event"
            @submit="onVerifyClick"
            @uploadClick="$refs.inputUpload.click()"
            @documentClick="onDocumentClick($event)"
            @dropUpload="uploadAdditionalDocs"
            @deleteClick="onDeleteClick($event)"
            @deleteAllClick="onDeleteClick(null)"
            @deleteCertificate="options.certificate = null"
          />
          <SignatureOptions
            :define.sync="options.define"
            :format.sync="options.format"
            :include-chain.sync="options.includeChain"
            :sig-type.sync="options.sigType"
            :algorithm.sync="options.algorithm"
            :certificate-added="!!options.certificate"
            class="content__options"
            @defineChange="changeDefineMethod"
            @formatChange="options.format = $event"
            @includeChainChange="options.includeChain = $event"
            @sigTypeChange="options.sigType = $event"
            @algorithmChange="options.algorithm = $event"
            @addCertificate="$refs.inputUploadCertificate.click()"
          />
        </div>
      </v-col>
    </v-row>
    <input
      v-show="false"
      ref="inputUpload"
      type="file"
      multiple
      @change="uploadAdditionalDocs($event.target.files)"
    >
    <input
      v-show="false"
      ref="inputUploadCertificate"
      type="file"
      :accept="FILE_FORMATS.CERTIFICATES"
      @change="uploadCertificate($event.target.files)"
    >
  </v-container>
</template>

<script>
import pdfViewer from 'vue-pdf-embed/dist/vue2-pdf-embed'
import DocumentList from '@/components/DocumentList'
import SignatureOptions from '@/components/SignatureOptions'
import { mapActions, mapState } from 'vuex'
import {
  apiPostDocument,
  apiResetDocument,
  apiResetAllDocuments,
  apiPostedDocumentsList,
  apiVerifyDocuments,
  apiGetPreview
} from '@/api/api'
import {
  asyncCallWithTimeout,
  browserIsIE,
  checkFilesForCertificate,
  fileToBase64,
  formDataFile,
  isArray,
  isNotEmptyObj
} from '@/helpers/helpers'
import { VerifyPackageRequest, ErrorMessage } from '@/helpers/mappers'
import { STEP, FILE_FORMATS } from '@/helpers/constants'
import { debounce } from 'lodash'
import { ResizeObserver } from 'vue-resize'

export default {
  name: 'SignatureDocManager',
  components: {
    pdfViewer, DocumentList, SignatureOptions, ResizeObserver
  },

  data () {
    return {
      STEP,
      FILE_FORMATS,
      docList: [],
      docListLoading: false,
      a4Height: {
        outer: '100%',
        inner: '100%'
      },
      pdf: {
        preview: null,
        previewedId: '',
        animate: 'idle',
        page: {
          current: 1,
          count: 0
        },
        size: {
          height: null,
          scale: 1.414
        }
      },
      options: {
        define: 'auto',
        format: 'PDF',
        includeChain: false,
        sigType: 'dataSign',
        algorithm: 'GR 34.11-2012 256',
        plugins: [],
        certificate: null
      },
      appConfig: this.$appConfig
    }
  },

  computed: {
    ...mapState({
      uploadedFiles: state => state.signature.files,
      step: state => state.signature.step,
      chainInclusionHint: state => state.policies.ChainInclusionHint ?? false,
      svsVersion: state => state.policies.Version ?? null
    }),

    previewWindowHeight () {
      return this.step !== STEP.PREVIEW || this.pdf.preview === 'error' || this.pdf.preview === 'none'
        ? this.a4Height.outer
        : 'auto'
    },
    viewerSrc () {
      return this.pdf.previewedId && this.pdf.preview !== 'error' && this.pdf.preview !== 'none'
        ? this.pdf.preview
        : ''
    }
  },

  async beforeMount () {
    window.addEventListener('resize', this.resizeContentPreview)

    if (!isArray(this.uploadedFiles)) {
      if (this.step === STEP.UPLOADING) {
        this.changeStep({ type: 'signature', step: STEP.IDLE })
        return
      }

      if (this.step === STEP.PREVIEW) { // Back from results
        this.docListLoading = true
        await this.getAllPostedDocs()
        this.docListLoading = false
      }
    } else { // Initial loading
      if (this.svsVersion === 2) {
        this.options.includeChain = this.chainInclusionHint
      }

      await this.postFilesAndGetDocumentList(this.uploadedFiles)
      this.changeStep({ type: 'signature', step: STEP.PREVIEW })
    }

    this.saveInitialFiles({ type: 'signature', files: null })

    if (!isArray(this.docList)) {
      if (Array.isArray(this.uploadedFiles) && this.uploadedFiles.length > 1) {
        this.showError('Не удалось получить список документов')
      }
      this.changeStep({ type: 'signature', step: STEP.IDLE })
      return
    }

    const index = this.docList.length - 1
    const loadPreview = async (index) => {
      const docId = this.docList[index].id

      const previewResult = await this.getPreview(docId)
      if (previewResult === 'error') {
        if (index > 0) {
          await loadPreview(--index)
        } else {
          this.pdf.previewedId = docId
          this.pdf.preview = 'error'
        }
      }
    }
    await loadPreview(index)
  },

  mounted () {
    this.resizeContentPreview()
  },

  methods: {
    debounce,
    browserIsIE,
    ...mapActions(['changeStep', 'resultDataSave', 'saveInitialFiles']),
    ...mapActions('notification', ['showAlert', 'showError']),

    async getAllPostedDocs () {
      try {
        const postedDocsList = await apiPostedDocumentsList()

        if (!isArray(postedDocsList)) {
          this.docList = []
          this.pdf.preview = null
          this.pdf.previewedId = ''
          this.changeStep({ type: 'signature', step: STEP.IDLE })
          return false
        }

        this.docList = postedDocsList.map(doc => {
          return {
            id: doc.id,
            name: doc.Name
          }
        })
        return true
      } catch (e) {
        console.error('getAllPostedDocs', e)
        return false
      }
    },

    uploadCertificate (files) {
      checkFilesForCertificate(files)
      this.options.certificate = files[0]
      if (!browserIsIE()) {
        this.$refs.inputUploadCertificate.value = null
      }
    },

    async uploadAdditionalDocs (files) {
      this.docListLoading = true

      for (const file of files) {
        await apiPostDocument(formDataFile(file), file.name)
      }
      await this.getAllPostedDocs()

      if (isArray(this.docList)) {
        const previewResult = await this.getPreview(this.docList[this.docList.length - 1].id)
        if (previewResult === 'error') {
          if (this.$refs.pdfViewer) {
            this.pdf.page.count = this.$refs.pdfViewer?.pageCount ?? 1
          }
          return
        }
      }
      this.$refs.inputUpload.value = null

      this.docListLoading = false
    },

    async postFilesAndGetDocumentList (files) {
      if (!files?.length) {
        return
      }

      for (const file of files) {
        await apiPostDocument(formDataFile(file), file.name)
      }
      await this.getAllPostedDocs()
    },

    resizeContentPreview () {
      const width = this.$refs.previewWindow?.clientWidth ?? 'auto'
      const height = width * 1.414
      this.a4Height.outer = `${(height - 24)}px`
      this.a4Height.inner = `${height - 64}px`
    },

    async onDocumentClick (id) {
      const previewResult = await this.getPreview(id)

      if (previewResult === 'error') {
        this.pdf.previewedId = id
        this.pdf.preview = 'error'
      }
    },

    async getPreview (docId) {
      if (!docId || !isArray(this.docList)) {
        this.changeStep({ type: 'signature', step: STEP.IDLE })
        return 'error'
      }

      if (docId === this.pdf.previewedId) {
        this.pdf.page.count = this.$refs.pdfViewer?.pageCount ?? 1
        return 'ok'
      }

      this.docListLoading = true
      this.pdf.animate = 'prepared'

      try {
        const pdfPreview = await asyncCallWithTimeout(
          apiGetPreview(docId),
          this.appConfig.previewLoadTimeout,
          'Загрузка превью (параметры)'
        )

        if (pdfPreview.type === 'application/json') {
          this.pdf.animate = 'idle'
          this.pdf.page.count = 0
          return 'error'
        }

        this.pdf.animate = 'launch'
        this.pdf.preview = URL.createObjectURL(pdfPreview)
        this.pdf.previewedId = docId
        this.pdf.page.count = this.$refs.pdfViewer?.pageCount ?? 1
        return 'ok'
      } catch (error) {
        if (isNotEmptyObj(error) && error.timeout && error.text) {
          console.error(error.text)

          this.pdf.animate = 'idle'
          this.pdf.page.count = 0
          return 'error'
        }

        const previewResponse = await apiGetPreview(docId, true)

        if (!previewResponse) {
          this.showError(`Ошибка превью: ${ErrorMessage(previewResponse)}`)
        }

        if (previewResponse && previewResponse !== 'success') {
          console.warn(`Не удалось отобразить превью: ${ErrorMessage(previewResponse)}`)
          if (previewResponse.Error === 'doc_not_found_id') {
            const listNotEmpty = await this.getAllPostedDocs()
            if (!listNotEmpty) {
              this.showAlert('Документ не найден, возможно данные устарели')
            }
          }
        }

        this.pdf.page.count = 0
        return 'error'
      } finally {
        this.pdf.page.current = 1
        this.docListLoading = false
      }
    },

    pdfCanvasElement () {
      try {
        if (
          this.$refs.pdfViewer?.$el.children?.length &&
          this.$refs.pdfViewer.$el.children[0].children?.length &&
          this.$refs.pdfViewer.$el.children[0].children[0].children?.length &&
          this.$refs.pdfViewer.$el.children[0].children[0].children[0]?.tagName === 'CANVAS'
        ) {
          return this.$refs.pdfViewer.$el.children[0].children[0].children[0]
        } else {
          return null
        }
      } catch (e) {
        console.warn(e)
        return null
      }
    },

    onContentResize: debounce(function () {
      // Обновляем фиксированную высоту обёртки для превью основываясь на соотношении сторон при изначальном рендере документа
      if (this.$refs.pdfViewer?.$el) {
        this.pdf.size.height = this.$refs.pdfViewer.$el.clientWidth * this.pdf.size.scale
      }

      // Ищем канвас для отрисовки pdf и выставляем ему адаптивные значения ширины и высоты
      if (this.pdfCanvasElement()) {
        const canvas = this.pdfCanvasElement()

        canvas.style.width = '100%'
        canvas.style.height = '100%'
      }
    }, 200),

    onPdfRendered () {
      // Проверка, что после рендера есть контент
      // Чтобы не реагировать на изменение высоты при рендере 'пустого' превью (pdf.preview === 'error')
      // todo Разделить параметр preview на preview и status, чтобы убрать этот костыль

      if (this.$refs.pdfViewer?.$el?.clientHeight > 0) {
        // Определяем количество страниц
        this.pdf.page.count = this.$refs.pdfViewer?.pageCount ?? 1

        // Выставляем элементу фиксированную высоту, а также запоминаем соотношение сторон документа
        this.pdf.size.height = this.$refs.pdfViewer?.$el?.clientHeight
        this.pdf.size.scale = this.$refs.pdfViewer?.$el?.clientHeight / this.$refs.pdfViewer?.$el?.clientWidth
      }
    },

    onRenderFailed: debounce(async function () {
      console.error('Произошла ошибка при рендере PDF, такое может произойти из-за быстрой смены страниц. Пытаюсь загрузить документ заново')
      const previewId = this.pdf.previewedId
      this.pdf.previewedId = ''
      await this.onDocumentClick(previewId)
    }, 500),

    async onVerifyClick () {
      this.docListLoading = true

      const options = { ...this.options, includeChain: this.options.includeChain ? 'true' : 'false' }

      try {
        let paramsToSend = { DocumentIds: this.docList.map(doc => doc.id) }
        let customParams = null

        if (this.options.define === 'manually' || options.includeChain === 'true') {
          let certificateBase64 = null

          if (this.options.certificate) {
            try {
              certificateBase64 = await fileToBase64(this.options.certificate)
            } catch (e) {
              console.error(e)
              this.showError('Не удалось распознать прикрепленный сертификат')
              this.docListLoading = false
              return
            }
          }

          if (certificateBase64) {
            options.certificate = certificateBase64
          }

          customParams = new VerifyPackageRequest(options)
        } else if (isArray(this.options.plugins)) {
          customParams = {
            CertVerifiersPluginsIds: new VerifyPackageRequest({ plugins: this.options.plugins }).CertVerifiersPluginsIds
          }
        }

        if (customParams) {
          paramsToSend = { ...paramsToSend, CustomParams: customParams }
        }

        const result = await apiVerifyDocuments(paramsToSend)

        if (result?.result) {
          this.resultDataSave({ type: 'signature', data: result })
          this.changeStep({ type: 'signature', step: STEP.RESULT })
        } else {
          this.showError(`Не удалось проверить документы с выбранными настройками: ${result?.errorMsg}`)

          const listNotEmpty = await this.getAllPostedDocs()
          if (!listNotEmpty) {
            this.showAlert('Документы не найдены, возможно данные устарели')
          }
        }
      } catch (e) {
        console.error('Возникла ошибка в функции отправки документов на проверку!', e)
      }

      this.docListLoading = false
    },

    async onDeleteClick (id) {
      this.docListLoading = true

      if (!id) {
        await apiResetAllDocuments()
      } else {
        this.docList = [...this.docList.filter(doc => doc.id !== id)]

        if (this.pdf.previewedId === id) {
          this.pdf.previewedId = ''
          this.pdf.preview = 'none'
        }

        await apiResetDocument(id)
      }

      await this.getAllPostedDocs()

      this.docListLoading = false
    },

    changeDefineMethod (method) {
      this.options.define = method
      if (method === 'auto') {
        this.options.certificate = null
      }
    },

    onPagesHover () {
      this.$refs.pagesBackground.style.backgroundColor = this.appConfig.color.mainLight
    },
    onPagesLeave () {
      this.$refs.pagesBackground.style.backgroundColor = 'transparent'
    }
  }
}
</script>

<style lang="scss" scoped>
  $paginator-height: 60px;
  $paginator-margin: 16px;

  .content {
    padding: 20px 5vw;

    @media(max-width: $cp-laptop-breakpoint) {
      display: block;
      padding: 20px 0;
    }

    &__preview {
      position: relative;
      height: 100%;
      background-color: white;
      border: 1px solid $color-divider;
      transition: height .5s ease;
      border-radius: $default-border-radius;
      overflow: hidden;
    }

    &__skeleton-preview {
      height: 100%;
      //height: calc(100% - #{$paginator-height + 5});
    }

    &__skeleton-list {
      height: 320px;

      @media(max-width: $cp-laptop-breakpoint) {
        height: auto;
      }
    }

    &__preview-empty {
      // margin-bottom: ($paginator-height + ($paginator-margin * 2));
      background-color: rgba(white, 0.8);
      border: 1px solid $color-divider;
      border-radius: $default-border-radius;

      &--error {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }
    }

    &__pdf {
      width: 100%;

      &--animate-show {
        animation: fadeOut 0.6s ease-in-out;
      }
    }

    &__pages {
      position: sticky;
      left: 0;
      bottom: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: transparent;
      margin-top: $paginator-margin;
      height: $paginator-height;
      opacity: 0;
      transition: opacity 0.5s ease-out;

      &__background {
        padding: 10px;
        border-radius: $default-border-radius;
      }

      &--animate {
        opacity: 1;
      }
    }

    &__row {
      height: 100%;

      @media(max-width: $cp-laptop-breakpoint) {
        height: auto;

        display: flex;
        flex-direction: column-reverse;
        flex-grow: initial;
        align-items: center;
      }
    }

    &__bottom-col {
      @media(max-width: $cp-laptop-breakpoint) {
        flex-basis: auto;
      }
    }

    &__list-and-options {
      position: sticky;
      top: 100px;

      &--ie {
        position: static;
        top: 0;
      }
    }
  }

  ::v-deep .v-skeleton-loader.v-skeleton-loader--is-loading {
    .v-skeleton-loader__image {
      height: 100%;
    }
  }
</style>
